import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

interface Branding {
  id: string;
  admin_id: string;
  company_name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string | null;
  background_style: string;
  background_value: string | null;
  button_style: string;
  font_family: string;
  show_company_info: boolean;
  welcome_title: string | null;
  welcome_message: string | null;
  custom_css: string | null;
}

interface BrandingContextType {
  branding: Branding | null;
  loading: boolean;
  refreshBranding: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const BrandingProvider = ({ children }: { children: ReactNode }) => {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchBranding = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('admin_branding')
        .select('*')
        .eq('admin_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching branding:', error);
      } else if (data) {
        setBranding(data);
        applyBranding(data);
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyBranding = (brandingData: Branding) => {
    const root = document.documentElement;

    // Apply primary color
    if (brandingData.primary_color) {
      const hsl = hexToHSL(brandingData.primary_color);
      root.style.setProperty('--primary', hsl);
    }

    // Apply secondary color
    if (brandingData.secondary_color) {
      const hsl = hexToHSL(brandingData.secondary_color);
      root.style.setProperty('--secondary', hsl);
    }

    // Apply font family
    if (brandingData.font_family) {
      root.style.setProperty('--font-family', getFontFamily(brandingData.font_family));
    }

    // Apply custom CSS
    if (brandingData.custom_css) {
      let styleElement = document.getElementById('custom-branding-css');
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'custom-branding-css';
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = brandingData.custom_css;
    }
  };

  const hexToHSL = (hex: string): string => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert hex to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    h = Math.round(h * 360);
    s = Math.round(s * 100);
    const lPercent = Math.round(l * 100);

    return `${h} ${s}% ${lPercent}%`;
  };

  const getFontFamily = (fontKey: string): string => {
    const fontMap: Record<string, string> = {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
      mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
      inter: '"Inter", sans-serif',
      poppins: '"Poppins", sans-serif',
      roboto: '"Roboto", sans-serif',
    };
    return fontMap[fontKey] || fontMap.sans;
  };

  useEffect(() => {
    fetchBranding();
  }, [user]);

  return (
    <BrandingContext.Provider value={{ branding, loading, refreshBranding: fetchBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
